import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import './Navbar.css';

// Comprehensive searchable index covering the entire website
const searchIndex = [
  // Services
  {
    title: "Fabrication and Welding",
    category: "Services",
    path: "/service#fabrication",
    keywords: ["fabrication", "welding", "steel", "mild steel", "stainless steel", "alloys", "structures", "assemblies", "metal work", "custom steel"],
    snippet: "High-precision welding and fabrication services tailored for structural and industrial needs."
  },
  {
    title: "Industrial Painting Work",
    category: "Services",
    path: "/service#painting",
    keywords: ["painting", "paint", "industrial painting", "coating", "heavy equipment", "protective", "aesthetic", "surface finish"],
    snippet: "High-performance painting solutions meeting industrial-grade standards for machinery and steel."
  },
  {
    title: "Mechanical Assembly",
    category: "Services",
    path: "/service#assembly",
    keywords: ["assembly", "mechanical assembly", "technicians", "components", "sub-assembly", "integration", "fitting"],
    snippet: "Accurate mechanical assembly and quality control from sub-assembly to full-system integration."
  },
  {
    title: "Shot Blasting with Copper Slag",
    category: "Services",
    path: "/service#blasting",
    keywords: ["blasting", "shot blasting", "copper slag", "abrasive", "surface cleanliness", "tanks", "pipelines", "prep"],
    snippet: "Copper slag abrasive blasting for superior surface cleanliness and anchor profiles."
  },
  {
    title: "Structural Erection",
    category: "Services",
    path: "/service#Irrigation",
    keywords: ["erection", "irrigation", "structural erection", "installation", "alignment", "frameworks", "platforms", "pipelines"],
    snippet: "Reliable structural erection services for steel fabrication and manufacturing plants."
  },
  {
    title: "Metalizing Work",
    category: "Services",
    path: "/service#metalizing",
    keywords: ["metalizing", "corrosion", "wear resistance", "coatings", "marine", "infrastructure", "surface integrity"],
    snippet: "Advanced metalizing services providing long-lasting corrosion and wear resistance."
  },
  // About Page
  {
    title: "About Reena Industries",
    category: "About",
    path: "/about",
    keywords: ["about", "company", "manufacturer", "precision components", "manufacturing", "overview", "founded", "profile"],
    snippet: "Leading manufacturer delivering precision components and innovative manufacturing solutions since 2021."
  },
  {
    title: "Mr. Abhay Prajapati (Director)",
    category: "About",
    path: "/about",
    keywords: ["director", "abhay", "abhay prajapati", "leadership", "founder", "experience", "engineering design", "production"],
    snippet: "From the Director's Desk: 20+ years of manufacturing experience in engineering design and production management."
  },
  {
    title: "Meet Our Team & Engineers",
    category: "About",
    path: "/about",
    keywords: ["team", "engineers", "pradeep", "pradeep prajapati", "anshu", "anshu prajapati", "operations manager", "chief engineer", "staff"],
    snippet: "Key leadership: Chief Engineer Pradeep Prajapati & Operations Manager Anshu Prajapati."
  },
  {
    title: "Our Mission & Vision",
    category: "About",
    path: "/about",
    keywords: ["mission", "vision", "values", "goals", "engineering excellence", "quality"],
    snippet: "Delivering engineering excellence and empowering industries with durable, advanced solutions."
  },
  // Contact & Careers
  {
    title: "Contact Us & Inquiry Form",
    category: "Contact",
    path: "/contact",
    keywords: ["contact", "message", "inquiry", "send message", "get in touch", "form", "whatsapp"],
    snippet: "Send us a message or reach out directly for project inquiries and quotes."
  },
  {
    title: "Careers & Hiring",
    category: "Contact",
    path: "/contact#careers",
    keywords: ["career", "careers", "job", "jobs", "hiring", "hire", "vacancy", "resume", "hr", "fabricators", "technicians", "engineers", "apply"],
    snippet: "Explore openings for Mechanical Engineers, Supervisors, Fabricators, and Technicians."
  },
  {
    title: "Office Location & Plant Address",
    category: "Contact",
    path: "/contact",
    keywords: ["location", "address", "map", "bhilai", "hathkhoj", "chhattisgarh", "factory", "plant", "engineering park"],
    snippet: "Plot no 30,31, Engineering park, Heavy industrial area, Hathkhoj Bhilai (CG) 490023, India."
  },
  {
    title: "Phone & Email Support",
    category: "Contact",
    path: "/contact",
    keywords: ["phone", "call", "mobile", "number", "email", "mail", "telephone", "contact info"],
    snippet: "Call: +91 9589161419 / +91 9752791914 | Email: info@reenaindustries.com"
  },
  // Home Page
  {
    title: "Home - Precision & Manufacturing",
    category: "Home",
    path: "/",
    keywords: ["home", "main", "solutions", "products", "innovative manufacturing", "engineer driven"],
    snippet: "Precision-engineered fabrication and manufacturing solutions meeting global quality standards."
  }
];

const popularSearches = ["Welding", "Painting", "Assembly", "Blasting", "Careers", "Team", "Contact"];

export default function Navbar(){
    const [query, setQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Helper to close Bootstrap navbar toggle on mobile/tablet
    const closeNavbar = () => {
        const navCollapse = document.getElementById('navbarSupportedContent');
        if (navCollapse && (navCollapse.classList.contains('show') || navCollapse.classList.contains('collapsing'))) {
            const bsCollapse = window.bootstrap?.Collapse?.getOrCreateInstance 
                ? window.bootstrap.Collapse.getOrCreateInstance(navCollapse, { toggle: false })
                : (window.bootstrap?.Collapse ? new window.bootstrap.Collapse(navCollapse, { toggle: false }) : null);

            if (bsCollapse) {
                bsCollapse.hide();
            } else {
                navCollapse.classList.remove('show');
                navCollapse.classList.remove('collapsing');
                const toggler = document.querySelector('.navbar-toggler');
                if (toggler) {
                    toggler.classList.add('collapsed');
                    toggler.setAttribute('aria-expanded', 'false');
                }
            }
        }
    };

    // Close navbar and search dropdown on route/hash changes and handle smooth anchor scrolling
    useEffect(() => {
        closeNavbar();
        setShowResults(false);
        if (location.hash) {
            const id = location.hash.replace('#', '');
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 150);
        }
    }, [location]);

    // Close navbar and search dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const navCollapse = document.getElementById('navbarSupportedContent');
            const toggler = document.querySelector('.navbar-toggler');
            if (
                navCollapse &&
                navCollapse.classList.contains('show') &&
                !navCollapse.contains(event.target) &&
                !toggler?.contains(event.target)
            ) {
                closeNavbar();
            }

            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // Filter & rank search results based on query
    const searchResults = useMemo(() => {
        const cleanQuery = query.trim().toLowerCase();
        if (!cleanQuery) return [];

        return searchIndex
            .map((item) => {
                let score = 0;
                const titleLower = item.title.toLowerCase();
                const snippetLower = item.snippet.toLowerCase();

                // Exact title match gets highest priority
                if (titleLower === cleanQuery) score += 100;
                else if (titleLower.startsWith(cleanQuery)) score += 50;
                else if (titleLower.includes(cleanQuery)) score += 30;

                // Keyword matches
                item.keywords.forEach((kw) => {
                    if (kw === cleanQuery) score += 40;
                    else if (kw.startsWith(cleanQuery)) score += 25;
                    else if (kw.includes(cleanQuery)) score += 15;
                });

                // Snippet match
                if (snippetLower.includes(cleanQuery)) score += 10;

                return { ...item, score };
            })
            .filter((item) => item.score > 0)
            .sort((a, b) => b.score - a.score);
    }, [query]);

    // Navigate to selected search result
    const handleSelectResult = (path) => {
        setShowResults(false);
        closeNavbar();
        setQuery('');

        const [, hash] = path.split('#');
        navigate(path);

        if (hash) {
            setTimeout(() => {
                const element = document.getElementById(hash);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 150);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const cleanQuery = query.trim().toLowerCase();
        if (!cleanQuery) return;

        if (searchResults.length > 0) {
            // Jump directly to top matching result
            handleSelectResult(searchResults[0].path);
        } else {
            // Show helpful suggestions in dropdown
            setShowResults(true);
        }
    };

  return (
    <nav className="navbar navbar-expand-lg custom-navbar">
        <div className="container-fluid px-md-3">
            <Link className="navbar-brand" to="/" onClick={closeNavbar}>
                <img src={logo} alt='logo' className='custom-logo'/>
            </Link>
            <button 
                className="navbar-toggler custom-toggler collapsed" 
                type="button" 
                data-bs-toggle="collapse" 
                data-bs-target="#navbarSupportedContent" 
                aria-controls="navbarSupportedContent" 
                aria-expanded="false" 
                aria-label="Toggle navigation"
            >
                <span className="toggler-bar top-bar"></span>
                <span className="toggler-bar middle-bar"></span>
                <span className="toggler-bar bottom-bar"></span>
            </button>
            <div 
                className="collapse navbar-collapse" 
                id="navbarSupportedContent"
                onClick={(e) => {
                    if (e.target.closest('a')) {
                        closeNavbar();
                    }
                }}
            >
                <ul className="navbar-nav ms-auto mb-2 mb-lg-0 mx-md-4 gap-lg-2">
                    <li className="nav-item">
                        <NavLink to="/" className="nav-link" end onClick={closeNavbar}>Home</NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink to='/about' className="nav-link" onClick={closeNavbar}>About</NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink to='/service' className="nav-link" onClick={closeNavbar}>Services</NavLink>
                    </li>
                    <li className="nav-item">
                        <NavLink to='/contact' className="nav-link" onClick={closeNavbar}>Contact</NavLink>
                    </li>
                </ul>

                {/* Site-Wide Search Form & Live Dropdown */}
                <div className="custom-search-wrapper" ref={searchRef}>
                    <form className="custom-search-container" onSubmit={handleSearch}>
                        <input 
                            className="custom-search" 
                            type="search" 
                            placeholder="Search site (e.g. welding, jobs)..." 
                            aria-label="Search"
                            value={query} 
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setShowResults(true);
                            }}
                            onFocus={() => {
                                if (query.trim()) setShowResults(true);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setShowResults(false);
                            }}
                            required
                        />
                        <button className="custom-btn" type="submit">Search</button>
                    </form>

                    {showResults && query.trim().length > 0 && (
                        <div className="search-results-dropdown">
                            {searchResults.length > 0 ? (
                                searchResults.map((item, idx) => (
                                    <div 
                                        key={idx} 
                                        className="search-result-item"
                                        onClick={() => handleSelectResult(item.path)}
                                    >
                                        <div className="search-result-header">
                                            <span className="search-result-title">{item.title}</span>
                                            <span className="search-result-category">{item.category}</span>
                                        </div>
                                        <p className="search-result-snippet">{item.snippet}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="search-no-results">
                                    <p>No results found for "<strong>{query}</strong>"</p>
                                    <div className="search-suggestions-label">Popular Searches:</div>
                                    <div className="search-suggestions-list">
                                        {popularSearches.map((tag, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                className="search-suggestion-badge"
                                                onClick={() => {
                                                    setQuery(tag);
                                                    setShowResults(true);
                                                }}
                                            >
                                                {tag}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </nav>
  );
}
